"use client";

import Link from "next/link";
import { useEffect, useState } from "react";

import { DeskBackButton } from "@/components/DeskBackButton";
import { ExpiryDeskWordmark } from "@/components/ExpiryDeskWordmark";
import { AppFeatureBulletList, SavingsSubline } from "@/components/pricing/PricingUiParts";
import {
  annualVsMonthlySavings,
  formatZarAmount,
  managerTierFeatures,
  ownerTierFeatures,
  stripeHintManager,
  stripeHintOwner,
  MANAGER_MONTHLY_ZAR,
  MANAGER_YEARLY_ZAR,
  OWNER_MONTHLY_ZAR,
  OWNER_YEARLY_ZAR,
} from "@/lib/pricingDisplay";
import { submitPayFastCheckout } from "@/lib/payfast/submitCheckout";
import { supabase } from "@/lib/supabaseClient";

const managerSave = annualVsMonthlySavings(MANAGER_MONTHLY_ZAR, MANAGER_YEARLY_ZAR);
const ownerSave = annualVsMonthlySavings(OWNER_MONTHLY_ZAR, OWNER_YEARLY_ZAR);

const teal = "#1DB8C4";

/**
 * In-app subscription hub: current plan, PayFast checkout, and upgrade paths.
 */
export function ManageSubscriptionView() {
  const [loadingPlan, setLoadingPlan] = useState<"pro" | "premium" | null>(null);
  const [managingPortal, setManagingPortal] = useState(false);
  const [error, setError] = useState("");
  const [billingNotice, setBillingNotice] = useState("");
  const [status, setStatus] = useState<{ plan?: string; status?: string; currentPeriodEnd?: string | null } | null>(null);

  async function startCheckout(plan: "pro" | "premium") {
    setLoadingPlan(plan);
    setError("");
    try {
      if (!supabase) throw new Error("Sign-in isn't available right now. Please try again in a moment.");
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error("Please sign in first.");

      const response = await fetch("/api/billing/create-checkout-session", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ plan, returnTo: "manage-subscription" }),
      });
      const data = await response.json();
      if (!response.ok) {
          throw new Error(data?.error || "Checkout didn't start. Please try again.");
      }
      submitPayFastCheckout(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Checkout failed");
      setLoadingPlan(null);
    }
  }

  async function openBillingPortal() {
    setManagingPortal(true);
    setError("");
    try {
      if (!supabase) throw new Error("Sign-in isn't available right now. Please try again in a moment.");
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error("Please sign in first.");

      const response = await fetch("/api/billing/create-portal-session", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || "We couldn't open billing right now. Please try again.");
      if (!data?.url) throw new Error("We couldn't open billing right now. Please try again.");
      window.location.href = data.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not open portal");
      setManagingPortal(false);
    }
  }

  async function loadStatus() {
    try {
      if (!supabase) return;
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) return;

      const response = await fetch("/api/billing/status", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (response.ok) setStatus(data || null);
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    void loadStatus();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    const billing = (url.searchParams.get("billing") || "").toLowerCase();
    if (billing === "cancelled") {
      setBillingNotice("You left checkout — nothing was charged.");
    } else if (billing === "success") {
      setBillingNotice("Payment received. Your plan updates in a moment — refresh once if needed.");
    }
    if (billing) {
      url.searchParams.delete("billing");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  const p = (status?.plan ?? "free").toLowerCase();
  const editionLabel =
    p === "premium" ? "Owner edition" : p === "pro" ? "Manager edition" : "Free plan";

  return (
    <div className="ed-manage-sub-shell">
      <div className="ed-manage-sub-inner">
        <div className="ed-manage-sub-toprow">
          <DeskBackButton />
          <ExpiryDeskWordmark variant="nav" showTagline={false} href="/dashboard" />
        </div>

        <header className="ed-manage-sub-hero">
          <p className="ed-manage-sub-eyebrow">Account</p>
          <h1 className="ed-manage-sub-title">Manage subscription</h1>
          <p className="ed-manage-sub-lead">
            Upgrade via PayFast, manage billing from pricing, or compare editions.
          </p>
        </header>

        {billingNotice ? (
          <div className="ed-manage-sub-banner ed-manage-sub-banner--success" role="status">
            <span className="ed-manage-sub-banner__text">{billingNotice}</span>
            <button type="button" onClick={() => setBillingNotice("")} className="ed-btn-ghost ed-manage-sub-banner__dismiss">
              Dismiss
            </button>
          </div>
        ) : null}

        {status ? (
          <section className="ed-card-glass ed-manage-sub-card ed-manage-sub-card--plan" aria-labelledby="ed-manage-current-label">
            <p id="ed-manage-current-label" className="ed-manage-sub-card__eyebrow">
              Current plan
            </p>
            <p className="ed-manage-sub-card__headline">
              {editionLabel} · {String(status.status || "active").replace(/_/g, " ")}
            </p>
            <p className="ed-manage-sub-card__sub">
              {status.currentPeriodEnd
                ? `Renews or ends ${new Date(status.currentPeriodEnd).toLocaleDateString("en-ZA", { dateStyle: "medium" })}`
                : "Renewal date appears when billing syncs."}
            </p>
          </section>
        ) : null}

        <section className="ed-card-glass ed-manage-sub-card ed-manage-sub-card--portal">
          <h2 className="ed-manage-sub-section-title">Billing &amp; cancellation</h2>
          <p className="ed-manage-sub-portal-copy">
            View pricing, cancel your PayFast subscription, or compare plans on the billing page.
          </p>
          <button
            type="button"
            onClick={() => void openBillingPortal()}
            disabled={managingPortal || loadingPlan !== null}
            aria-busy={managingPortal}
            className="ed-btn-primary ed-manage-sub-portal-cta"
          >
            {managingPortal ? "Opening…" : "Manage billing"}
          </button>
        </section>

        <p className="ed-manage-sub-plans-eyebrow">Change plan</p>

        <div className="ed-manage-sub-plan-grid" aria-label="Upgrade options">
          <section className="ed-card-glass ed-manage-sub-plan ed-manage-sub-plan--featured">
            <h2 className="ed-manage-sub-plan__label">Manager edition</h2>
            <p className="ed-manage-sub-plan__price">
              {formatZarAmount(MANAGER_MONTHLY_ZAR)}
              <span className="ed-manage-sub-plan__suffix"> / mo</span>
            </p>
            <SavingsSubline
              yearlyZar={MANAGER_YEARLY_ZAR}
              save={managerSave}
              mutedColor="var(--ed-text-secondary)"
              tealColor={teal}
              navyColor="var(--ed-text)"
              fontSize={13}
            />
            <p className="ed-manage-sub-plan__blurb">
              Branch-scale capture with a single trail — includes expanded pill counter allowance.
            </p>
            <AppFeatureBulletList features={managerTierFeatures} color="var(--ed-text-secondary)" />
            <button
              type="button"
              onClick={() => void startCheckout("pro")}
              disabled={loadingPlan !== null}
              aria-busy={loadingPlan === "pro"}
              className="ed-btn-primary ed-manage-sub-plan__cta"
            >
              {loadingPlan === "pro" ? "One moment…" : "Subscribe as Manager"}
            </button>
            <p className="ed-manage-sub-plan__fineprint">{stripeHintManager()}</p>
          </section>

          <section className="ed-card-glass ed-manage-sub-plan ed-manage-sub-plan--owner">
            <h2 className="ed-manage-sub-plan__label">Owner edition</h2>
            <p className="ed-manage-sub-plan__price">
              {formatZarAmount(OWNER_MONTHLY_ZAR)}
              <span className="ed-manage-sub-plan__suffix"> / mo</span>
            </p>
            <SavingsSubline
              yearlyZar={OWNER_YEARLY_ZAR}
              save={ownerSave}
              mutedColor="var(--ed-text-secondary)"
              tealColor={teal}
              navyColor="var(--ed-text)"
              fontSize={13}
            />
            <p className="ed-manage-sub-plan__blurb">
              Unlimited pill counter, unlimited scans, and room for the full network story.
            </p>
            <AppFeatureBulletList features={ownerTierFeatures} color="var(--ed-text-secondary)" />
            <button
              type="button"
              onClick={() => void startCheckout("premium")}
              disabled={loadingPlan !== null}
              aria-busy={loadingPlan === "premium"}
              className="ed-manage-sub-plan__cta ed-manage-sub-plan__cta--owner"
            >
              {loadingPlan === "premium" ? "One moment…" : "Subscribe as Owner"}
            </button>
            <p className="ed-manage-sub-plan__fineprint">{stripeHintOwner()}</p>
          </section>
        </div>

        <p className="ed-manage-sub-footer">
          Want the full comparison table?{" "}
          <Link href="/pricing" className="ed-manage-sub-footer-link">
            View pricing
          </Link>
        </p>

        {error ? <div className="ed-manage-sub-banner ed-manage-sub-banner--error">{error}</div> : null}
      </div>
    </div>
  );
}
